import { Size, Switch } from "@next-platform/client-common-spicy-ui";
import type { RulebookPublicView } from "@next-platform/packages-ts-types";
import React, { useCallback, useMemo } from "react";

import Trans from "@/components/Trans/Trans";

import { HUD_LINE, HUD_MUTED } from "./hudClassNames";

type RulebookSheetFooterProps = {
  rulebook: RulebookPublicView;
  characterFirstName: string;
  /** Whether the conversation's Rulebook evaluation is frozen (interactive features off). */
  isFrozen: boolean;
  /** False when there is no conversation yet — disables the settings controls. */
  settingsEnabled: boolean;
  onToggleFrozen: (frozen: boolean) => void;
  onToggleHidden: (isHidden: boolean) => void;
};

/** End of the sheet: hidden-meter note, which Rulebook this is, and the interactive-features switch. */
const RulebookSheetFooter = ({
  rulebook,
  characterFirstName,
  isFrozen,
  settingsEnabled,
  onToggleFrozen,
  onToggleHidden,
}: RulebookSheetFooterProps) => {
  const nameValues = useMemo(() => ({ name: characterFirstName }), [characterFirstName]);
  const rulebookValues = useMemo(
    () => ({ name: rulebook.name, version: rulebook.version, creator: rulebook.creator_username ?? "" }),
    [rulebook.name, rulebook.version, rulebook.creator_username],
  );
  const handleInteractiveChange = useCallback((isInteractive: boolean) => onToggleFrozen(!isInteractive), [onToggleFrozen]);

  return (
    <div data-testid="RulebookSheetFooter" className="flex flex-col gap-3.5">
      {rulebook.has_hidden_meters ? (
        <p data-testid="RulebookExplanationSheet-HiddenNote" className={`text-pretty text-xs leading-normal ${HUD_MUTED}`}>
          <Trans
            i18nKey="chat:rulebook.hud.sheet.hiddenMeters"
            defaults="{{name}} also reacts to things the creator keeps hidden."
            values={nameValues}
          />
        </p>
      ) : null}
      <p data-testid="RulebookExplanationSheet-RulebookLine" className={`text-[11px] leading-normal ${HUD_MUTED}`}>
        {rulebook.creator_username ? (
          <Trans
            i18nKey="chat:rulebook.hud.sheet.rulebookBy"
            defaults="Rulebook: {{name}} v{{version}} by @{{creator}}. Regenerating or switching a reply restores the meters from that point."
            values={rulebookValues}
          />
        ) : (
          <Trans
            i18nKey="chat:rulebook.hud.sheet.rulebook"
            defaults="Rulebook: {{name}} v{{version}}. Regenerating or switching a reply restores the meters from that point."
            values={rulebookValues}
          />
        )}
      </p>
      <div className={`flex items-center gap-3.5 border-t pt-2.5 ${HUD_LINE}`}>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            <Trans i18nKey="chat:rulebook.hud.sheet.interactive" defaults="Interactive features" />
          </div>
          <div className={`mt-0.5 text-xs leading-[1.4] ${HUD_MUTED}`}>
            <Trans
              i18nKey="chat:rulebook.hud.sheet.interactiveHelp"
              defaults="Turn off to chat with {{name}} as a regular character. Meters freeze where they are."
              values={nameValues}
            />
          </div>
        </div>
        <Switch
          data-testid="RulebookHud-InteractiveToggle"
          size={Size.SMALL}
          checked={!isFrozen}
          onCheckedChange={handleInteractiveChange}
          disabled={!settingsEnabled}
        />
      </div>
      <div className={`flex items-center gap-3.5 border-t pt-2.5 ${HUD_LINE} ${isFrozen ? "opacity-45" : ""}`}>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">
            <Trans i18nKey="chat:rulebook.hud.sheet.hideMeters" defaults="Hide meters" />
          </div>
          <div className={`mt-0.5 text-xs leading-[1.4] ${HUD_MUTED}`}>
            <Trans
              i18nKey="chat:rulebook.hud.sheet.hideMetersHelp"
              defaults="Removes the meters from the screen. They keep updating, and still shape how {{name}} responds."
              values={nameValues}
            />
          </div>
        </div>
        {/* Visible only while the meters are shown, so the switch reads "off"; the chat menu shows them again. */}
        <Switch
          data-testid="RulebookHud-HideToggle"
          size={Size.SMALL}
          checked={false}
          onCheckedChange={onToggleHidden}
          disabled={!settingsEnabled || isFrozen}
        />
      </div>
    </div>
  );
};

export default React.memo(RulebookSheetFooter);
